import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Home, Car, ShieldCheck, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Top bar */}
      <div className="border-b border-slate-100">
        <div className="container-main py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-900"
            data-testid="about-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
            <span>spotfinder.co</span>
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#e8f8eb] via-white to-white pointer-events-none" />
        <div className="container-main relative py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#5ebc67]/10 text-[#4da855] text-xs font-semibold tracking-wider uppercase mb-6">
              <MapPin className="w-3 h-3" />
              Sydney · est. 2026
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05] mb-6">
              About SpotFinder
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              A Sydney-based marketplace that connects homeowners with unused driveways
              to drivers looking for affordable, convenient parking.
            </p>
          </div>
        </div>
      </section>

      {/* Origin story */}
      <section className="py-16 md:py-24 border-t border-slate-100">
        <div className="container-main">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
            <div className="md:col-span-4">
              <p className="text-sm uppercase tracking-wider text-[#5ebc67] font-semibold mb-3">The story</p>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
                The idea came from a simple observation.
              </h2>
            </div>
            <div className="md:col-span-8 space-y-5 text-slate-700 text-lg leading-relaxed">
              <p>
                Streets full of cars searching for parking, and driveways sitting completely empty.
              </p>
              <p className="text-slate-900 font-semibold">
                The solution was obvious. So we built it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container-main">
          <div className="max-w-2xl mb-12">
            <p className="text-sm uppercase tracking-wider text-[#5ebc67] font-semibold mb-3">What we do</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              SpotFinder handles everything.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5 md:gap-8">
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-[#5ebc67]/10 flex items-center justify-center mb-5">
                <Home className="w-6 h-6 text-[#4da855]" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">For homeowners</h3>
              <p className="text-slate-600 leading-relaxed">
                List your space, set your availability and price, and get paid directly. Your driveway becomes income.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-[#5ebc67]/10 flex items-center justify-center mb-5">
                <Car className="w-6 h-6 text-[#4da855]" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">For drivers</h3>
              <p className="text-slate-600 leading-relaxed">
                Find and book a spot in minutes. Skip the circling, the meters, and the surprise fines.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-[#5ebc67]/10 flex items-center justify-center mb-5">
                <ShieldCheck className="w-6 h-6 text-[#4da855]" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Payments</h3>
              <p className="text-slate-600 leading-relaxed">
                Processed securely through Stripe. Hosts are paid promptly. Drivers' cards stay protected.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Closing — Sydney first */}
      <section className="py-20 md:py-28">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 text-white text-xs font-semibold tracking-wider uppercase mb-6">
              <MapPin className="w-3 h-3" />
              Starting in Sydney
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-5">
              This is just the beginning.
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-10">
              We're starting where the problem is loudest — and where the driveways are sitting empty.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => navigate('/search')}
                className="bg-[#5ebc67] hover:bg-[#4da855] text-white rounded-full px-8 py-6 text-base font-semibold shadow-sm"
                data-testid="about-cta-find"
              >
                Find a parking spot
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/create-listing')}
                variant="outline"
                className="rounded-full px-8 py-6 text-base font-semibold border-slate-300"
                data-testid="about-cta-list"
              >
                List your driveway
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer link row */}
      <footer className="py-10 border-t border-slate-100">
        <div className="container-main flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">© 2026 SpotFinder. Sydney's parking marketplace.</p>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="text-sm text-slate-500 hover:text-slate-900">Terms</Link>
            <Link to="/privacy" className="text-sm text-slate-500 hover:text-slate-900">Privacy</Link>
            <Link to="/about" className="text-sm text-slate-900 font-semibold">About</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
